import type { BestNextMoveBundle, ProjectMissionSnapshot, ProjectProgressSnapshot } from '../contracts/types'
import { BNM_ORBIS } from './bestNextMoveOrbis'
import { ORBIS_MISSION_ID } from './missionPresets'
import { PROGRESS_ORBIS } from './progressOrbis'

export const ACME_FILM_MISSION_ID = 'acme-film' as const
export const SCAPES_DEMO_MISSION_ID = 'scapes-demo' as const
export const OMNISTUDIO_PACKAGE_MISSION_ID = 'omnistudio-package' as const
export const OMNICINE_TIMELINE_MISSION_ID = 'omnicine-timeline' as const
export const OMNICAST_REVIEW_MISSION_ID = 'omnicast-review' as const

const ACME_PROGRESS: ProjectProgressSnapshot = {
  projectId: ACME_FILM_MISSION_ID,
  projectName: 'ACME Protocol',
  projectType: 'film_story',
  currentFocusStageId: 'intent_framed',
  summary: {
    readyCount: 2,
    totalCountable: 8,
    headline: 'Story framed — building scene spine',
  },
  spine: [
    { stageId: 'signal_captured', status: 'ready', label: 'Signal Captured', ordinal: 1, primarySurface: 'guided_studio' },
    { stageId: 'intent_framed', status: 'in_progress', label: 'Story Framed', ordinal: 2, primarySurface: 'guided_studio' },
    { stageId: 'structure_coherent', status: 'future', label: 'Story Structure', ordinal: 3, primarySurface: 'guided_studio' },
  ],
  markers: [],
}

const ACME_BNM: BestNextMoveBundle = {
  generatedAt: '2026-06-03T00:00:00.000Z',
  primary: {
    recommendationId: 'bnm-acme-01',
    category: 'continue_building',
    title: 'Continue ACME Protocol structure',
    summary: 'Ground the ACME storyline before timeline or presentation paths.',
    whyThisMatters: 'This project is independent of ORBIS; progress must stay scoped to ACME.',
    riskIfIgnored: 'Handoffs may reference the wrong narrative package.',
    suggestedSurface: 'guided_studio',
    suggestedAction: 'Continue in Guided Studio',
    suggestedStageId: 'intent_framed',
    affectedProjectId: ACME_FILM_MISSION_ID,
    priority: 'high',
    confidence: 'medium',
    humanConfirmationRequired: false,
    allowedActions: ['start', 'defer', 'dismiss'],
    forbiddenActions: ['auto_execute', 'auto_publish', 'auto_handoff', 'sign_in', 'create_account', 'launch_app'],
    blockersAddressed: [],
  },
}

const SCAPES_PROGRESS: ProjectProgressSnapshot = {
  projectId: SCAPES_DEMO_MISSION_ID,
  projectName: 'Northern Scape',
  projectType: 'scape_first',
  currentFocusStageId: 'world_or_scape_defined',
  summary: {
    readyCount: 1,
    totalCountable: 5,
    headline: 'World anchors in progress',
  },
  spine: [
    {
      stageId: 'world_or_scape_defined',
      status: 'in_progress',
      label: 'World Defined',
      ordinal: 1,
      primarySurface: 'scapes_worlds',
    },
  ],
  markers: [],
}

const SCAPES_BNM: BestNextMoveBundle = {
  generatedAt: '2026-06-03T00:00:00.000Z',
  primary: {
    recommendationId: 'bnm-scapes-demo-01',
    category: 'prepare_scape_world',
    title: 'Anchor Northern Scape environments',
    summary: 'Define spatial rules and environment anchors for the Scape layer.',
    whyThisMatters: 'Scape readiness gates XR and presentation pathways.',
    riskIfIgnored: 'Spatial continuity may break across scenes.',
    suggestedSurface: 'scapes_worlds',
    suggestedAction: 'Open Scapes workspace',
    affectedProjectId: SCAPES_DEMO_MISSION_ID,
    priority: 'high',
    confidence: 'medium',
    humanConfirmationRequired: false,
    allowedActions: ['start', 'defer', 'dismiss'],
    forbiddenActions: ['auto_execute', 'auto_publish', 'auto_handoff', 'sign_in', 'create_account', 'launch_app'],
    blockersAddressed: [],
  },
}

function orbisSnapshot(): ProjectMissionSnapshot {
  return {
    projectId: ORBIS_MISSION_ID,
    title: 'ORBIS',
    projectType: PROGRESS_ORBIS.projectType,
    progress: { ...PROGRESS_ORBIS },
    bestNextMove: { ...BNM_ORBIS, generatedAt: BNM_ORBIS.generatedAt },
    activeSurface: 'guided_studio',
  }
}

/**
 * Explicit read-model registry entries — not a failure fallback.
 * Firestore may override when project docs exist (read-only adapter).
 */
const OMNISTUDIO_BNM: BestNextMoveBundle = {
  generatedAt: '2026-06-03T00:00:00.000Z',
  primary: {
    recommendationId: 'bnm-omnistudio-pkg-01',
    category: 'prepare_handoff',
    title: 'Compile structure package',
    summary: 'Review OmniStudio handoff readiness and missing dependencies.',
    whyThisMatters: 'Production compile depends on a coherent structure package.',
    riskIfIgnored: 'Handoff may stall downstream timeline work.',
    suggestedSurface: 'omnistudio',
    suggestedAction: 'Open OmniStudio',
    affectedProjectId: OMNISTUDIO_PACKAGE_MISSION_ID,
    priority: 'high',
    confidence: 'medium',
    humanConfirmationRequired: false,
    allowedActions: ['start', 'defer', 'dismiss'],
    forbiddenActions: ['auto_execute', 'auto_publish', 'auto_handoff', 'sign_in', 'create_account', 'launch_app'],
    blockersAddressed: [],
  },
}

const OMNICINE_BNM: BestNextMoveBundle = {
  generatedAt: '2026-06-03T00:00:00.000Z',
  primary: {
    recommendationId: 'bnm-omnicine-tl-01',
    category: 'prepare_timeline',
    title: 'Close timeline sequence gaps',
    summary: 'Review scene order and cinematic presentation risk in OmniCine.',
    whyThisMatters: 'Timeline coherence affects screening readiness.',
    riskIfIgnored: 'Presentation cuts may read disjointed.',
    suggestedSurface: 'omnicine',
    suggestedAction: 'Open OmniCine',
    affectedProjectId: OMNICINE_TIMELINE_MISSION_ID,
    priority: 'high',
    confidence: 'medium',
    humanConfirmationRequired: false,
    allowedActions: ['start', 'defer', 'dismiss'],
    forbiddenActions: ['auto_execute', 'auto_publish', 'auto_handoff', 'sign_in', 'create_account', 'launch_app'],
    blockersAddressed: [],
  },
}

const OMNICAST_BNM: BestNextMoveBundle = {
  generatedAt: '2026-06-03T00:00:00.000Z',
  primary: {
    recommendationId: 'bnm-omnicast-rev-01',
    category: 'review_stage',
    title: 'Witness presentation readiness',
    summary: 'Confirm read-only witness visibility and feedback boundaries.',
    whyThisMatters: 'Review sessions must stay non-destructive.',
    riskIfIgnored: 'Feedback may be misinterpreted as approval.',
    suggestedSurface: 'omnicast',
    suggestedAction: 'Open OmniCast witness',
    affectedProjectId: OMNICAST_REVIEW_MISSION_ID,
    priority: 'medium',
    confidence: 'high',
    humanConfirmationRequired: true,
    allowedActions: ['review', 'defer', 'dismiss'],
    forbiddenActions: ['auto_execute', 'auto_publish', 'auto_handoff', 'sign_in', 'create_account', 'launch_app'],
    blockersAddressed: [],
  },
}

export const PROJECT_SNAPSHOT_REGISTRY: Readonly<Record<string, ProjectMissionSnapshot>> = Object.freeze({
  [ACME_FILM_MISSION_ID]: {
    projectId: ACME_FILM_MISSION_ID,
    title: 'ACME Protocol',
    projectType: 'film_story',
    progress: ACME_PROGRESS,
    bestNextMove: ACME_BNM,
    activeSurface: 'guided_studio',
  },
  [SCAPES_DEMO_MISSION_ID]: {
    projectId: SCAPES_DEMO_MISSION_ID,
    title: 'Northern Scape',
    projectType: 'scape_first',
    progress: SCAPES_PROGRESS,
    bestNextMove: SCAPES_BNM,
    activeSurface: 'scapes_worlds',
  },
  [OMNISTUDIO_PACKAGE_MISSION_ID]: {
    projectId: OMNISTUDIO_PACKAGE_MISSION_ID,
    title: 'Structure Package Alpha',
    projectType: 'studio_pack',
    progress: {
      projectId: OMNISTUDIO_PACKAGE_MISSION_ID,
      projectName: 'Structure Package Alpha',
      projectType: 'studio_pack',
      currentFocusStageId: 'omnistudio_handoff_ready',
      summary: { readyCount: 1, totalCountable: 4, headline: 'Compile readiness in review' },
      spine: [
        {
          stageId: 'omnistudio_handoff_ready',
          status: 'in_progress',
          label: 'OmniStudio Handoff',
          primarySurface: 'omnistudio',
        },
      ],
      markers: [],
    },
    bestNextMove: OMNISTUDIO_BNM,
    activeSurface: 'omnistudio',
  },
  [OMNICINE_TIMELINE_MISSION_ID]: {
    projectId: OMNICINE_TIMELINE_MISSION_ID,
    title: 'Sequence Cut 04',
    projectType: 'timeline_production',
    progress: {
      projectId: OMNICINE_TIMELINE_MISSION_ID,
      projectName: 'Sequence Cut 04',
      projectType: 'timeline_production',
      currentFocusStageId: 'omnicine_timeline_ready',
      summary: { readyCount: 0, totalCountable: 6, headline: 'Timeline gaps under review' },
      spine: [
        {
          stageId: 'omnicine_timeline_ready',
          status: 'in_progress',
          label: 'OmniCine Timeline',
          primarySurface: 'omnicine',
        },
      ],
      markers: [],
    },
    bestNextMove: OMNICINE_BNM,
    activeSurface: 'omnicine',
  },
  [OMNICAST_REVIEW_MISSION_ID]: {
    projectId: OMNICAST_REVIEW_MISSION_ID,
    title: 'Screening Witness',
    projectType: 'film_story',
    progress: {
      projectId: OMNICAST_REVIEW_MISSION_ID,
      projectName: 'Screening Witness',
      projectType: 'film_story',
      currentFocusStageId: 'omnicast_presentation_ready',
      summary: { readyCount: 1, totalCountable: 3, headline: 'Presentation witness open' },
      spine: [
        {
          stageId: 'omnicast_presentation_ready',
          status: 'in_progress',
          label: 'Presentation Ready',
          primarySurface: 'omnicast',
        },
      ],
      markers: [],
    },
    bestNextMove: OMNICAST_BNM,
    activeSurface: 'omnicast',
  },
})

export function getRegisteredProjectSnapshot(projectId: string): ProjectMissionSnapshot | null {
  if (projectId === ORBIS_MISSION_ID) {
    return orbisSnapshot()
  }
  const entry = PROJECT_SNAPSHOT_REGISTRY[projectId]
  if (!entry) return null
  return {
    ...entry,
    progress: { ...entry.progress, spine: [...entry.progress.spine] },
    bestNextMove: {
      ...entry.bestNextMove,
      primary: { ...entry.bestNextMove.primary },
    },
  }
}
